<template>
  <Content>
    <div class="container py-4">
      <!-- وضعیت لودینگ -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-teal" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- پیام خطا یا محصول پیدا نشد -->
      <div
        v-else-if="errorMessage"
        class="alert text-center my-4 py-4 border-0 shadow-sm"
        :class="isNotFound ? 'alert-info bg-light text-muted' : 'alert-danger'"
        role="alert"
      >
        <i
          :class="
            isNotFound
              ? 'bi bi-box-seam fs-2 d-block mb-2 text-teal'
              : 'bi bi-exclamation-triangle fs-2 d-block mb-2 text-danger'
          "
        ></i>
        <span class="fw-semibold">{{ errorMessage }}</span>
      </div>

      <!-- محتوای اصلی محصول -->
      <div v-else-if="product">
        <!-- بردکرامب دسته‌بندی -->
        <nav
          v-if="categoryBreadcrumbs.length > 0"
          aria-label="breadcrumb"
          class="mb-4"
        >
          <ol class="breadcrumb custom-breadcrumb p-3 rounded-4 shadow-sm mb-0">
            <li class="breadcrumb-item">
              <router-link
                :to="currentLang.urls.home"
                class="text-decoration-none breadcrumb-link"
              >
                <i class="bi bi-house-door me-1"></i>
                {{ currentLang.home }}
              </router-link>
            </li>
            <li
              v-for="(cat, index) in categoryBreadcrumbs"
              :key="cat.id"
              class="breadcrumb-item"
              :class="{ active: index === categoryBreadcrumbs.length - 1 }"
            >
              <router-link
                v-if="index !== categoryBreadcrumbs.length - 1"
                :to="`${currentLang.urls.ProductCategory}/${cat.slug}`"
                class="text-decoration-none breadcrumb-link"
              >
                {{ cat.title }}
              </router-link>
              <span v-else class="text-teal fw-bold">{{ cat.title }}</span>
            </li>
          </ol>
        </nav>

        <!-- بخش تصویر و مشخصات محصول -->
        <div class="card main-product-card border-0 shadow-sm p-3 p-md-4 mb-4">
          <div class="row g-4 align-items-center">
            <!-- گالری تصویر -->
            <div class="col-12 col-md-5">
              <div
                class="main-image-container rounded-4 overflow-hidden position-relative shadow-sm"
              >
                <img
                  v-if="selectedImage"
                  :src="selectedImage"
                  :alt="productTitle"
                  class="product-image-fluid"
                  @error="handleImageError"
                />
                <div
                  v-else
                  class="placeholder-image d-flex align-items-center justify-content-center text-muted"
                >
                  <i class="bi bi-image fs-1 opacity-50"></i>
                </div>

                <!-- دکمه علاقه‌مندی شناور روی تصویر -->
                <button
                  class="btn btn-wishlist-float position-absolute top-0 end-0 m-3 shadow-sm"
                  :class="{ active: isWishlisted }"
                  :title="
                    isWishlisted
                      ? currentLang.removeFromWishlist
                      : currentLang.addToWishlist
                  "
                  @click="toggleWishlist"
                >
                  <i
                    :class="isWishlisted ? 'bi bi-heart-fill' : 'bi bi-heart'"
                  ></i>
                </button>

                <!-- نشانگر نوع محصول -->
                <span
                  v-if="product.is_book"
                  class="badge product-type-badge position-absolute top-0 start-0 m-3"
                >
                  <i class="bi bi-book me-1"></i>
                  {{ currentLang.book }}
                </span>
              </div>

              <!-- تامب‌نیل‌ها -->
              <div
                v-if="product.images && product.images.length > 1"
                class="d-flex gap-2 mt-3 overflow-auto pb-1"
              >
                <div
                  v-for="img in product.images"
                  :key="img.id"
                  class="thumbnail-wrapper rounded-3 overflow-hidden cursor-pointer"
                  :class="{ active: selectedImage === img.url }"
                  @click="selectedImage = img.url"
                >
                  <img
                    :src="img.url"
                    :alt="productTitle"
                    class="w-100 h-100 object-fit-cover"
                  />
                </div>
              </div>
            </div>

            <!-- مشخصات و خرید -->
            <div class="col-12 col-md-7 d-flex flex-column h-100">
              <div class="mb-2">
                <span
                  v-if="product.category?.title"
                  class="badge category-span"
                >
                  {{ product.category.title }}
                </span>
              </div>

              <h1 class="fw-bold fs-3 text-dark-blue mb-3 product-title">
                {{ productTitle }}
              </h1>

              <div class="mb-4">
                <span
                  v-if="product.stock > 0"
                  class="badge bg-stock-in px-3 py-2 rounded-pill"
                >
                  <i class="bi bi-check-circle-fill me-1"></i>
                  {{ currentLang.stock }}: {{ formatNumber(product.stock) }}
                </span>
                <span v-else class="badge bg-stock-out px-3 py-2 rounded-pill">
                  <i class="bi bi-x-circle-fill me-1"></i>
                  {{ currentLang.outOfStock }}
                </span>
              </div>

              <!-- قیمت و دکمه‌ها -->
              <div class="price-action-box p-3 p-md-4 rounded-4 mt-auto">
                <div
                  class="d-flex justify-content-between align-items-center mb-3"
                >
                  <span class="text-muted fw-semibold">
                    {{ currentLang.price }}
                  </span>
                  <span class="fw-bold fs-4 text-price">
                    {{ formatPrice(product.price) }}
                  </span>
                </div>

                <div class="row g-2">
                  <div class="col-12 col-sm-6">
                    <button
                      class="btn btn-cart w-100 py-2 d-flex align-items-center justify-content-center gap-2 rounded-3"
                      :disabled="product.stock === 0"
                      @click="addToCart"
                    >
                      <i class="bi bi-cart-plus fs-5"></i>
                      <span>{{ currentLang.addToCart }}</span>
                    </button>
                  </div>

                  <!-- دکمه دانلود کل ویدیو و کلیپ -->
                  <div class="col-12 col-sm-6">
                    <button
                      class="btn btn-cart w-100 py-2 d-flex align-items-center justify-content-center gap-2 rounded-3"
                      @click="downloadAllVideos"
                    >
                      <i class="bi bi-cloud-arrow-down-fill fs-5"></i>
                      <span class="single-line-text">{{
                        currentLang.downloadAllVideos
                      }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- بخش تب‌های محصول (توضیحات، سرفصل‌ها، نظرات) -->
        <div class="card border-0 shadow-sm p-4 rounded-4 mb-4">
          <!-- هدر تب‌ها -->
          <ul class="nav custom-product-tabs border-bottom pb-3 mb-4 gap-2">
            <li class="nav-item">
              <button
                class="nav-link custom-tab-btn"
                :class="{ active: activeTab === 'description' }"
                @click="activeTab = 'description'"
              >
                <i class="bi bi-card-text me-1"></i>
                {{ currentLang.description }}
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link custom-tab-btn"
                :class="{ active: activeTab === 'chapters' }"
                @click="activeTab = 'chapters'"
              >
                <i class="bi bi-journal-bookmark me-1"></i>
                {{ currentLang.chapters }}
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link custom-tab-btn"
                :class="{ active: activeTab === 'comments' }"
                @click="activeTab = 'comments'"
              >
                <i class="bi bi-chat-left-text me-1"></i>
                {{ currentLang.reviews }}
              </button>
            </li>
          </ul>

          <!-- محتوای تب‌ها -->
          <div class="tab-content">
            <!-- تب ۱: توضیحات -->
            <div v-if="activeTab === 'description'" class="tab-pane-content">
              <div
                v-if="productDescription"
                class="product-description lh-lg text-secondary"
              >
                {{ productDescription }}
              </div>
              <div v-else class="text-muted text-center py-4">
                {{ currentLang.noDescription }}
              </div>
            </div>

            <!-- تب ۲: سرفصل‌ها -->
            <div v-else-if="activeTab === 'chapters'" class="tab-pane-content">
              <div
                v-if="product.chapters && product.chapters.length > 0"
                class="chapters-accordion d-flex flex-column gap-3"
              >
                <div
                  v-for="(chapter, index) in product.chapters"
                  :key="chapter.id"
                  class="chapter-item rounded-3 overflow-hidden border"
                >
                  <!-- هدر سرفصل (آکاردئون) -->
                  <div
                    class="chapter-header p-3 d-flex justify-content-between align-items-center cursor-pointer"
                    :class="{
                      'chapter-header-open': isChapterOpen(chapter.id),
                    }"
                    @click="toggleChapter(chapter.id)"
                  >
                    <div class="d-flex align-items-center gap-3">
                      <span class="chapter-number rounded-circle fw-bold">
                        {{ formatNumber(index + 1) }}
                      </span>
                      <h3 class="fs-6 fw-bold mb-0 text-dark-blue">
                        {{ getChapterName(chapter) }}
                      </h3>
                    </div>

                    <div class="d-flex align-items-center gap-3">
                      <span
                        v-if="chapter.media?.length"
                        class="badge bg-light text-secondary rounded-pill border px-2 py-1 small"
                      >
                        {{ formatNumber(chapter.media.length) }}
                        {{ currentLang.mediaCount }}
                      </span>
                      <i
                        class="bi"
                        :class="
                          isChapterOpen(chapter.id)
                            ? 'bi-chevron-up'
                            : 'bi-chevron-down'
                        "
                      ></i>
                    </div>
                  </div>

                  <!-- محتوای داخل سرفصل (لیست مدیاها) -->
                  <div
                    v-show="isChapterOpen(chapter.id)"
                    class="p-3 bg-white border-top"
                  >
                    <ul
                      v-if="chapter.media && chapter.media.length > 0"
                      class="list-unstyled mb-0 d-flex flex-column gap-2"
                    >
                      <li
                        v-for="item in chapter.media"
                        :key="item.id"
                        class="media-row d-flex flex-wrap align-items-center justify-content-between p-2 p-md-3 rounded-3 gap-2"
                      >
                        <!-- عنوان و آیکون تایپ مدیا -->
                        <div class="d-flex align-items-center gap-3">
                          <span
                            class="media-icon-wrapper rounded-circle d-flex align-items-center justify-content-center"
                            :class="{
                              'bg-clip': item.type === 'clip',
                              'bg-audio': item.type === 'audio',
                              'bg-game': item.type === 'game',
                            }"
                          >
                            <i
                              class="bi fs-5"
                              :class="{
                                'bi-play-circle-fill': item.type === 'clip',
                                'bi-music-note-beamed': item.type === 'audio',
                                'bi-controller': item.type === 'game',
                              }"
                            ></i>
                          </span>

                          <span class="fw-semibold text-dark-blue fs-6">
                            {{ item.title }}
                          </span>
                        </div>

                        <!-- دکمه اکشن متناسب با نوع مدیا -->
                        <button
                          class="btn btn-sm d-flex align-items-center gap-1 rounded-pill px-3"
                          :class="{
                            'btn-action-clip': item.type === 'clip',
                            'btn-action-audio': item.type === 'audio',
                            'btn-action-game': item.type === 'game',
                          }"
                          @click="handleMediaAction(item)"
                        >
                          <i
                            class="bi"
                            :class="{
                              'bi-play-fill': item.type === 'clip',
                              'bi-volume-up-fill': item.type === 'audio',
                              'bi-joystick': item.type === 'game',
                            }"
                          ></i>
                          <span>
                            {{
                              item.type === "clip"
                                ? currentLang.watchClip
                                : item.type === "audio"
                                ? currentLang.playAudio
                                : currentLang.playGame
                            }}
                          </span>
                        </button>
                      </li>
                    </ul>

                    <div v-else class="text-muted text-center py-3 small">
                      {{ currentLang.noMediaInChapter }}
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="text-muted text-center py-4">
                <i
                  class="bi bi-journal-x fs-1 text-muted opacity-50 d-block mb-2"
                ></i>
                <p class="mb-0">{{ currentLang.noChapters }}</p>
              </div>
            </div>

            <!-- تب ۳: نظرات -->
            <div v-else-if="activeTab === 'comments'" class="tab-pane-content">
              <div class="row g-4">
                <!-- ستون لیست نظرات -->
                <div class="col-lg-7">
                  <h4 class="fs-6 fw-bold text-dark-blue mb-3">
                    {{ currentLang.commentsTitle }} ({{
                      formatNumber(comments.length)
                    }})
                  </h4>

                  <div
                    v-if="comments.length > 0"
                    class="d-flex flex-column gap-3"
                  >
                    <div
                      v-for="item in comments"
                      :key="item.id"
                      class="comment-card p-3 rounded-3 border bg-white"
                    >
                      <div
                        class="d-flex justify-content-between align-items-center mb-2"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <div
                            class="avatar-circle rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                          >
                            {{
                              item.user_name
                                ? item.user_name.charAt(0).toUpperCase()
                                : "U"
                            }}
                          </div>
                          <span class="fw-bold text-dark-blue">{{
                            item.user_name || currentLang.userFallback
                          }}</span>
                        </div>
                        <span class="text-muted small">{{
                          item.created_at
                        }}</span>
                      </div>

                      <!-- ستاره‌های امتیاز -->
                      <div class="rating-stars mb-2">
                        <i
                          v-for="star in 5"
                          :key="star"
                          class="bi"
                          :class="
                            star <= item.rating
                              ? 'bi-star-fill text-warning'
                              : 'bi-star text-muted opacity-50'
                          "
                        ></i>
                      </div>

                      <p class="mb-0 text-secondary lh-base fs-6">
                        {{ item.content }}
                      </p>
                    </div>
                  </div>

                  <div
                    v-else
                    class="text-muted text-center py-4 bg-light rounded-3"
                  >
                    <i
                      class="bi bi-chat-square-text fs-2 text-muted opacity-50 d-block mb-2"
                    ></i>
                    <p class="mb-0">{{ currentLang.noComments }}</p>
                  </div>
                </div>

                <!-- ستون فرم ثبت نظر -->
                <div class="col-lg-5">
                  <div
                    class="comment-form-card p-3 p-md-4 rounded-3 border bg-white shadow-sm"
                  >
                    <h4 class="fs-6 fw-bold text-dark-blue mb-3">
                      {{ currentLang.submitComment }}
                    </h4>

                    <!-- پیام موفقیت -->
                    <div
                      v-if="commentSuccessMessage"
                      class="alert alert-success py-2 px-3 small rounded-3 mb-3"
                    >
                      <i class="bi bi-check-circle-fill me-1"></i>
                      {{ currentLang.commentSuccess }}
                    </div>

                    <form
                      @submit.prevent="handleCommentSubmit"
                      class="d-flex flex-column gap-3"
                    >
                      <!-- انتخاب امتیاز -->
                      <div>
                        <label
                          class="form-label small fw-semibold text-secondary mb-1"
                        >
                          {{ currentLang.yourRating }}
                        </label>
                        <div class="star-rating-select d-flex gap-1">
                          <i
                            v-for="star in 5"
                            :key="star"
                            class="bi fs-5 cursor-pointer transition-all"
                            :class="
                              star <= newComment.rating
                                ? 'bi-star-fill text-warning'
                                : 'bi-star text-muted'
                            "
                            @click="handleRating(star)"
                          ></i>
                        </div>
                      </div>

                      <!-- فیلد نام -->
                      <div>
                        <label
                          class="form-label small fw-semibold text-secondary mb-1"
                        >
                          {{ currentLang.yourName }}
                          <span class="text-danger">*</span>
                        </label>
                        <input
                          v-model="newComment.user_name"
                          type="text"
                          class="form-control"
                          :placeholder="currentLang.namePlaceholder"
                          required
                        />
                      </div>

                      <!-- فیلد متن نظر -->
                      <div>
                        <label
                          class="form-label small fw-semibold text-secondary mb-1"
                        >
                          {{ currentLang.yourComment }}
                          <span class="text-danger">*</span>
                        </label>
                        <textarea
                          v-model="newComment.content"
                          rows="4"
                          class="form-control"
                          :placeholder="currentLang.commentPlaceholder"
                          required
                        ></textarea>
                      </div>

                      <!-- دکمه ارسال -->
                      <button
                        type="submit"
                        class="btn btn-primary d-flex align-items-center justify-content-center gap-2 rounded-3 mt-1"
                        :disabled="isSubmittingComment"
                      >
                        <span
                          v-if="isSubmittingComment"
                          class="spinner-border spinner-border-sm"
                        ></span>
                        <i v-else class="bi bi-send"></i>
                        <span>{{ currentLang.sendBtn }}</span>
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footerAdmin></footerAdmin>
  </Content>
</template>

<script setup>
import Content from "@/components/AdminContent.vue";
import footerAdmin from "@/components/AdminFooter.vue";
import { ref, onMounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import api from "@/plugins/axios";
import { lang } from "@/plugins/lang";

import enText from "@/locales/en/product";
import faText from "@/locales/fa/product";

const route = useRoute();

const translations = {
  en: enText,
  fa: faText,
};

const currentLang = computed(() => translations[lang.value]);

const product = ref(null);
const loading = ref(false);
const errorMessage = ref("");
const isNotFound = ref(false);
const selectedImage = ref("");
const isWishlisted = ref(false);
const activeTab = ref("description");
/*
|--------------------------------------------------------------------------
| مقادیر محاسباتی
|--------------------------------------------------------------------------
*/
const productTitle = computed(() => {
  if (!product.value) return "";
  return lang.value === "fa"
    ? product.value.name_fa || product.value.name_en
    : product.value.name_en || product.value.name_fa;
});

const productDescription = computed(() => {
  if (!product.value) return "";
  return lang.value === "fa"
    ? product.value.description_fa || product.value.description_en
    : product.value.description_en || product.value.description_fa;
});

const categoryBreadcrumbs = computed(() => {
  if (!product.value?.category) return [];

  const crumbs = [];
  let curr = product.value.category;

  while (curr) {
    crumbs.unshift({
      id: curr.id,
      title: curr.title,
      slug: curr.slug,
    });
    curr = curr.parents;
  }

  return crumbs;
});

/*
|--------------------------------------------------------------------------
| دریافت مشخصات محصول از API
|--------------------------------------------------------------------------
*/
const getProduct = async () => {
  loading.value = true;
  errorMessage.value = "";
  isNotFound.value = false;

  try {
    const slug = route?.params?.slug || "happy-steps-1-work-book";
    const response = await api.get(`/general/product/${slug}`);

    if (response.data?.success && response.data?.data) {
      product.value = response.data.data;

      if (product.value.images && product.value.images.length > 0) {
        selectedImage.value = product.value.images[0].url;
      } else {
        selectedImage.value = "";
      }
    } else {
      product.value = null;
      errorMessage.value =
        response.data?.message || currentLang.value.noProducts;
      isNotFound.value = true;
    }
  } catch (error) {
    console.error("Error fetching product details:", error);
    product.value = null;

    if (error.response?.status === 404) {
      isNotFound.value = true;
      errorMessage.value = currentLang.value.noProducts;
    } else {
      isNotFound.value = false;
      errorMessage.value =
        error.response?.data?.message || currentLang.value.serverError;
    }
  } finally {
    loading.value = false;
  }
};

/*
|--------------------------------------------------------------------------
| توابع کمکی و رویدادها
|--------------------------------------------------------------------------
*/
const formatPrice = (price) => {
  if (price === null || price === undefined)
    return `۰ ${currentLang.value.toman}`;

  const formattedNum = Number(price).toLocaleString(
    lang.value === "fa" ? "fa-IR" : "en-US"
  );
  return `${formattedNum} ${currentLang.value.toman}`;
};

const formatNumber = (num) => {
  if (num === null || num === undefined) return "0";
  return Number(num).toLocaleString(lang.value === "fa" ? "fa-IR" : "en-US");
};

const handleImageError = (e) => {
  e.target.style.display = "none";
};

const addToCart = () => {
  console.log("Add to cart:", product.value?.id);
};

const toggleWishlist = () => {
  isWishlisted.value = !isWishlisted.value;
  // متد اصلی ارسال درخواست به سرور را اینجا تکمیل نمایید
  console.log("Toggle wishlist status:", isWishlisted.value, product.value?.id);
};

const downloadAllVideos = () => {
  // متد نمونه جهت تکمیل بعدی توسط شما
  console.log("Download all videos for product:", product.value?.id);
};

/*
|--------------------------------------------------------------------------
| واچ‌ها و لایف‌سایکل
|--------------------------------------------------------------------------
*/
onMounted(() => {
  getProduct();
});

if (route) {
  watch(
    () => route.params?.slug,
    () => {
      getProduct();
    }
  );
}

// نگهداری آیدی‌های سرفصل‌های باز شده
const openChapters = ref([]);

const toggleChapter = (id) => {
  const index = openChapters.value.indexOf(id);
  if (index > -1) {
    openChapters.value.splice(index, 1);
  } else {
    openChapters.value.push(id);
  }
};

const isChapterOpen = (id) => openChapters.value.includes(id);

// نام سرفصل دو زبانه بر اساس name_fa و name_en
const getChapterName = (chapter) => {
  if (!chapter) return "";
  return lang.value === "fa"
    ? chapter.name_fa || chapter.name_en
    : chapter.name_en || chapter.name_fa;
};

// اکشن کلیک روی هر مدیا بر اساس تایپ
const handleMediaAction = (media) => {
  if (!media.link) return;

  if (media.type === "game") {
    // باز کردن بازی در تب جدید
    window.open(media.link, "_blank");
  } else {
    // برای فیلم یا صوت، باز کردن لینک مستقیم (یا اتصال به پلیر در آینده)
    window.open(media.link, "_blank");
  }
};

// دیتای تستی نظرات
const comments = ref([
  {
    id: 1,
    user_name: "سارا احمدی",
    rating: 5,
    created_at: "۱۴۰۲/۱۱/۱۵",
    content:
      "محتوای بسیار عالی و با کیفیتی داشت، خصوصاً بخش فایل‌های صوتی خیلی به یادگیری کمک کرد.",
  },
  {
    id: 2,
    user_name: "Ali Rezaei",
    rating: 4,
    created_at: "۱۴۰۲/۱۱/۲۰",
    content:
      "کتاب خیلی کاربردی بود، فقط اگر بازی‌های تعاملی بیشتری داشت جذاب‌تر می‌شد.",
  },
]);

// استیت فرم ثبت نظر
const newComment = ref({
  user_name: "",
  rating: 5,
  content: "",
});

const isSubmittingComment = ref(false);
const commentSuccessMessage = ref(false);

const handleRating = (rate) => {
  newComment.value.rating = rate;
};

// ثبت فرم نظر (به صورت محلی)
const handleCommentSubmit = () => {
  if (!newComment.value.user_name || !newComment.value.content) return;

  isSubmittingComment.value = true;

  setTimeout(() => {
    comments.value.unshift({
      id: Date.now(),
      user_name: newComment.value.user_name,
      rating: newComment.value.rating,
      created_at: new Date().toLocaleDateString(
        lang.value === "fa" ? "fa-IR" : "en-US"
      ),
      content: newComment.value.content,
    });

    // ریست فرم
    newComment.value = {
      user_name: "",
      rating: 5,
      content: "",
    };

    isSubmittingComment.value = false;
    commentSuccessMessage.value = true;

    setTimeout(() => {
      commentSuccessMessage.value = false;
    }, 4000);
  }, 600);
};
</script>

<style scoped>
.text-teal {
  color: #00cdc4 !important;
}

.text-dark-blue {
  color: #18324b;
}

.text-price {
  color: #18324b;
}

.cursor-pointer {
  cursor: pointer;
}

/* تصویر محصول */
.main-image-container {
  width: 100%;
  height: 380px;
  background-color: #ffffff;
  border: 1px solid #edf2f7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image-fluid {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  padding: 12px;
  transition: transform 0.3s ease;
}

.main-image-container:hover .product-image-fluid {
  transform: scale(1.03);
}

.placeholder-image {
  width: 100%;
  height: 100%;
  background: #f8fafc;
}

.thumbnail-wrapper {
  width: 70px;
  height: 70px;
  border: 2px solid #e2e8f0;
  background: #fff;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.thumbnail-wrapper.active,
.thumbnail-wrapper:hover {
  border-color: #00cdc4;
  transform: translateY(-2px);
}

/* دکمه شناور علاقه‌مندی */
.btn-wishlist-float {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all 0.25s ease;
  z-index: 2;
}

.btn-wishlist-float:hover,
.btn-wishlist-float.active {
  color: #e11d48;
  border-color: #fecdd3;
  background-color: #fff1f2;
  transform: scale(1.1);
}

/* بردکرامب */
.custom-breadcrumb {
  background: #ffffff;
  font-size: 0.9rem;
}

.breadcrumb-link {
  color: #64748b;
  transition: color 0.2s ease;
}

.breadcrumb-link:hover {
  color: #00cdc4;
}

/* کارت اصلی و عنوان */
.main-product-card {
  border-radius: 20px;
  background: #ffffff;
}

.product-title {
  line-height: 1.4;
  word-break: break-word;
}

.product-type-badge {
  background: rgba(24, 50, 75, 0.85);
  color: #ffffff;
  font-weight: 500;
  border-radius: 8px;
  padding: 6px 12px;
}

.category-span {
  background: #6cceff;
  color: #18324b;
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
}

/* وضعیت موجودی */
.bg-stock-in {
  background-color: #e6fffa;
  color: #00a8a0;
  font-weight: 600;
}

.bg-stock-out {
  background-color: #fee2e2;
  color: #dc2626;
  font-weight: 600;
}

/* باکس خرید و قیمت */
.price-action-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.btn-cart {
  background: #00cdc4;
  color: #ffffff;
  border: none;
  font-weight: 600;
  box-shadow: 0 6px 16px rgba(0, 205, 196, 0.25);
  transition: all 0.25s ease;
}

.btn-cart:hover:not(:disabled) {
  background: linear-gradient(135deg, #00cdc4 0%, #00a8a0 100%);
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 205, 196, 0.35);
}

.btn-cart:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-outline-teal {
  border: 1.5px solid #00cdc4;
  color: #00cdc4;
  background: #ffffff;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-outline-teal:hover {
  background: #00cdc4;
  color: #ffffff;
}

.btn-wishlist-active {
  border-color: #fda4af;
  background-color: #fff1f2;
}

/* توضیحات */
.product-description {
  white-space: pre-line;
  font-size: 0.98rem;
}

/* تب‌های اختصاصی */
.custom-product-tabs {
  border-color: #edf2f7 !important;
}

.custom-tab-btn {
  border: none;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 10px 20px;
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.custom-tab-btn:hover {
  color: #18324b;
  background: #f1f5f9;
}

.custom-tab-btn.active {
  background: #00cdc4 !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(0, 205, 196, 0.25);
}

.tab-pane-content {
  animation: fadeIn 0.25s ease-in-out;
}

/* آکاردئون سرفصل‌ها */
.chapter-item {
  border-color: #edf2f7 !important;
  background: #ffffff;
}

.chapter-header {
  background: #f8fafc;
  user-select: none;
  transition: background 0.2s ease;
}

.chapter-header:hover {
  background: #f1f5f9;
}

.chapter-header-open {
  background: #e6fffa !important;
}

.chapter-number {
  width: 32px;
  height: 32px;
  background: #ffffff;
  color: #18324b;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
}

/* سطرهای مدیا */
.media-row {
  background: #f8fafc;
  border: 1px solid #eef2f6;
  transition: all 0.2s ease;
}

.media-row:hover {
  background: #ffffff;
  border-color: #00cdc4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.media-icon-wrapper {
  width: 40px;
  height: 40px;
}

/* رنگ‌بندی آیکون‌ها بر اساس نوع */
.bg-clip {
  background-color: #e6fffa;
  color: #00cdc4;
}

.bg-audio {
  background-color: #fef3c7;
  color: #d97706;
}

.bg-game {
  background-color: #ede9fe;
  color: #7c3aed;
}

/* استایل دکمه‌های عملیاتی */
.btn-action-clip {
  background-color: #00cdc4;
  color: #ffffff;
}
.btn-action-clip:hover {
  background-color: #00a8a0;
  color: #ffffff;
}

.btn-action-audio {
  background-color: #f59e0b;
  color: #ffffff;
}
.btn-action-audio:hover {
  background-color: #d97706;
  color: #ffffff;
}

.btn-action-game {
  background-color: #8b5cf6;
  color: #ffffff;
}
.btn-action-game:hover {
  background-color: #7c3aed;
  color: #ffffff;
}

/* نظرات */
.comment-card {
  border-color: #edf2f7 !important;
  background-color: #ffffff;
}

.avatar-circle {
  width: 36px;
  height: 36px;
  background-color: #00cdc4;
  font-size: 0.9rem;
}

.star-rating-select i {
  cursor: pointer;
  transition: transform 0.15s ease;
}

.star-rating-select i:hover {
  transform: scale(1.15);
}

.comment-form-card {
  border-color: #edf2f7 !important;
  position: sticky;
  top: 90px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
